import { HtmlParser } from '@calendly/html-parser'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { Link } from '@calendly/ui/components/link'
import { Popup, Body, Footer } from '@calendly/ui/components/popup'
import moment from 'moment-timezone'
import { useSelector } from 'react-redux'

import { dateNotationSelector } from 'selectors/current_user'
import { userDateFormat } from 'services/time_format'

import { DATA_REQUEST_STATES } from './constants'


const Details = ({ request }) => {
  if (request.state === DATA_REQUEST_STATES.ERROR) {
    return <ErrorDetails />
  }

  if (request.state === DATA_REQUEST_STATES.CANCELED) {
    return <CanceledDetails request={request} />
  }

  switch (request.type) {
    case 'InviteesDeletion':
      return <InviteesDeletionDetails request={request} />
    case 'EventsDeletion':
      return <EventsDeletionDetails request={request} />
    case 'UserDeletion':
      return <UserDeletionDetails request={request} />
    default:
      return null
  }
}

const ContactSupportLink = ({ children }) => <Link href="mailto:support@calendly.com">{children}</Link>

const CanceledDetails = ({ request }) => {
  const dateNotation = useSelector(dateNotationSelector)

  return (
    <div data-testid="sub-description">
      {I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.view_details.canceled_description', {
        date: userDateFormat(moment(request.updated_at).tz('UTC'), dateNotation, 'date_small_with_year')
      })}
    </div>
  )
}

const ErrorDetails = () => (
  <>
    <div data-testid="sub-description">
      {I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.view_details.error_description')}
    </div>
    <div data-testid="sub-description">
      {HtmlParser(
        I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.view_details.error_contact'),
        { ContactSupportLink }
      )}
    </div>
  </>
)

const CompletedAt = ({ request }) => {
  const dateNotation = useSelector(dateNotationSelector)

  if (request.state === DATA_REQUEST_STATES.FINISHED) {
    return (
      <div>
        <strong>
          {I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.completed_at')}
        </strong>
        {` ${userDateFormat(moment(request.completed_at).tz('UTC'), dateNotation, 'date_small_with_year')}`}
      </div>
    )
  }

  return null
}

const InviteesDeletionDetails = ({ request }) => {
  const description = [DATA_REQUEST_STATES.PENDING, DATA_REQUEST_STATES.RUNNING].includes(request.state)
    ? I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.view_details.invitee_descriptions.incomplete')
    : I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.view_details.invitee_descriptions.completed')

  return (
    <>
      <div data-testid="sub-description">{description}</div>
      {request.emails && request.emails.map((email) => (
        <div key={email}>{email}</div>
      ))}
      <CompletedAt request={request} />
    </>
  )
}

const EventsDeletionDetails = ({ request }) => {
  const dateNotation = useSelector(dateNotationSelector)

  const attrs = {
    start_time: userDateFormat(moment(request.start_time).tz('UTC'), dateNotation, 'date_small_with_year'),
    end_time: userDateFormat(moment(request.end_time).tz('UTC'), dateNotation, 'date_small_with_year')
  }

  const description = [DATA_REQUEST_STATES.PENDING, DATA_REQUEST_STATES.RUNNING].includes(request.state)
    ? I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.view_details.events_descriptions.incomplete', attrs)
    : I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.view_details.events_descriptions.completed', attrs)

  return (
    <>
      <div data-testid="sub-description">{HtmlParser(description)}</div>
      <CompletedAt request={request} />
    </>
  )
}

const UserDeletionDetails = ({ request }) => (
  <>
    <div data-testid="sub-description">
      {I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.view_details.user_description')}
    </div>
    <CompletedAt request={request} />
  </>
)


export const ViewDetailsPopup = ({ request, onClose }) => {
  const dateNotation = useSelector(dateNotationSelector)

  return (
    <Popup
      onClose={onClose}
      title={I18n.t('publisher.organization_settings.data_deletion.section.deletion_requests.view_details.title', {
        date: userDateFormat(moment(request.created_at), dateNotation, 'date_small_with_year')
      })}
    >
      <Body>
        <Details request={request} />
      </Body>
      <Footer>
        <Button onClick={onClose}>
          {I18n.t('publisher.action.okay')}
        </Button>
      </Footer>
    </Popup>
  )
}
